import React, { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import {
  CalendarClock, Plus, Pencil, Trash2, Eye, Send, Loader2,
  ToggleLeft, ToggleRight, Users, X, Zap,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Mon=0 … Sun=6 — matches Python's datetime.weekday(), which is what
// reports._due_now() compares against. Do not reorder.
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const newId = () =>
  (window.crypto?.randomUUID?.() || `cr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

const blankReport = () => ({
  id: newId(),
  label: "",
  enabled: false,
  target_type: "group",
  target: "",
  recipients: [],
  send_at: "17:30",
  days: [0, 1, 2, 3, 4],
  skip_if_quiet: true,
  show_value: true,
  sections: { attention: true, open_book: true },
  last_run_at: null,
});

function Mark({ svg, color, size = 14 }) {
  if (svg) {
    return (
      <span
        className="inline-flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full"
        style={{ width: size, height: size }}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    );
  }
  return (
    <span
      className="rounded-full shrink-0 inline-block"
      style={{ width: size * 0.55, height: size * 0.55, background: color || "#94a3b8" }}
    />
  );
}

function daysLabel(days) {
  if (!days || days.length === 0) return "Every day";
  if (days.length === 7) return "Every day";
  const sorted = [...days].sort((a, b) => a - b);
  if (sorted.join() === "0,1,2,3,4") return "Mon–Fri";
  return sorted.map(d => DAYS[d]).join(" ");
}

export default function ClientReportsPanel({ reports, onChange, savedIds, clients, clientGroups }) {
  const [editing, setEditing] = useState(null);     // working copy in the modal
  const [busyId, setBusyId] = useState(null);
  const list = reports || [];

  const targetLabel = (r) => {
    if (r.target_type === "group") {
      return (clientGroups || []).find(g => g.id === r.target)?.name || "— pick a group —";
    }
    return r.target || "— pick a client —";
  };

  const replace = (id, patch) =>
    onChange(list.map(r => (r.id === id ? { ...r, ...patch } : r)));

  const remove = (r) => {
    if (!window.confirm(`Delete "${r.label || "this report"}"? Recipients stop receiving it.`)) return;
    onChange(list.filter(x => x.id !== r.id));
  };

  const commitEdit = () => {
    if (!editing.label.trim()) return toast.error("Give the report a name");
    if (!editing.target) return toast.error("Pick a client or a group");
    const exists = list.some(r => r.id === editing.id);
    onChange(exists ? list.map(r => (r.id === editing.id ? editing : r)) : [...list, editing]);
    setEditing(null);
  };

  // Preview / test read the report from SAVED settings, so an unsaved row has
  // nothing to read. Rather than silently sending the wrong thing, the buttons
  // stay disabled until Save Settings has run.
  const isSaved = (r) => (savedIds || []).includes(r.id);

  const preview = (r) => window.open(`${API}/api/email/client-report-preview?id=${r.id}`, "_blank");

  const sendTest = async (r) => {
    setBusyId(r.id);
    try {
      const res = await axios.post(`${API}/api/email/client-report-test`, { id: r.id });
      toast.success(res.data.message || "Test sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send test");
    } finally {
      setBusyId(null);
    }
  };

  const runNow = async (r) => {
    if (!window.confirm(`Send "${r.label}" to its ${r.recipients.length} recipient(s) now?`)) return;
    setBusyId(r.id);
    try {
      const res = await axios.post(`${API}/api/email/client-report-run`, { id: r.id });
      const msg = {
        sent: "Report sent",
        quiet: "Nothing to report — quiet day, nothing sent",
        "no-recipients": "No recipients configured",
        "no-target": "No target clients on this report",
        limit: "Daily email cap reached — not sent",
        error: "Email service error — not sent",
      }[res.data.status] || res.data.status;
      (res.data.status === "sent" ? toast.success : toast.info)(msg);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to run report");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rl-glass-card p-4 rounded-xl border border-[hsl(var(--border))] mb-3">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-sm font-semibold flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-sky-400" />
            Client Reports
            {list.filter(r => r.enabled).length > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-600 border border-sky-500/20">
                {list.filter(r => r.enabled).length} active
              </span>
            )}
          </p>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
            A daily end-of-day report for one client or client group — every PO line that moved, what happened to it, and what needs attention.
          </p>
        </div>
        <button
          onClick={() => setEditing(blankReport())}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold shrink-0 transition-colors"
          style={{ background: "rgba(14,165,233,0.08)", color: "#0ea5e9", border: "1px solid rgba(14,165,233,0.2)" }}
          data-testid="add-client-report-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Add report
        </button>
      </div>

      {list.length === 0 ? (
        <div className="p-4 rounded-lg border border-dashed border-[hsl(var(--border))] text-center">
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            No client reports yet. Add one to send a client's daily movement to their account team.
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {list.map(r => (
            <div
              key={r.id}
              className="flex items-center gap-2 p-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20"
              data-testid={`client-report-row-${r.id}`}
            >
              <button
                onClick={() => replace(r.id, { enabled: !r.enabled })}
                className={`shrink-0 ${r.enabled ? "text-emerald-500" : "text-[hsl(var(--muted-foreground))]"}`}
                title={r.enabled ? "Enabled — sending on schedule" : "Disabled"}
              >
                {r.enabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
              </button>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{r.label || "Untitled report"}</p>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">
                  {r.target_type === "group" ? "Group" : "Client"}: {targetLabel(r)}
                  {"  ·  "}{r.send_at} IST · {daysLabel(r.days)}
                  {"  ·  "}{(r.recipients || []).length} recipient{(r.recipients || []).length !== 1 ? "s" : ""}
                  {r.skip_if_quiet ? "  ·  skips quiet days" : ""}
                </p>
              </div>

              <div className="flex items-center gap-0.5 shrink-0">
                <button
                  onClick={() => preview(r)}
                  disabled={!isSaved(r)}
                  title={isSaved(r) ? "Preview in a new tab" : "Save settings first"}
                  className="p-1.5 rounded hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-sky-500 disabled:opacity-30"
                >
                  <Eye className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => sendTest(r)}
                  disabled={!isSaved(r) || busyId === r.id}
                  title={isSaved(r) ? "Send a test copy to me" : "Save settings first"}
                  className="p-1.5 rounded hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-emerald-500 disabled:opacity-30"
                >
                  {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                </button>
                <button
                  onClick={() => runNow(r)}
                  disabled={!isSaved(r) || busyId === r.id || !(r.recipients || []).length}
                  title={isSaved(r) ? "Send to the real recipients now" : "Save settings first"}
                  className="p-1.5 rounded hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-amber-500 disabled:opacity-30"
                >
                  <Zap className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setEditing({ ...blankReport(), ...r })}
                  title="Configure"
                  className="p-1.5 rounded hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-orange-500"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => remove(r)}
                  title="Delete report"
                  className="p-1.5 rounded hover:bg-red-500/10 text-[hsl(var(--muted-foreground))] hover:text-red-500"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {list.some(r => !isSaved(r)) && (
        <p className="text-[11px] text-amber-600 mt-2">
          Unsaved report — hit Save Settings to schedule it and enable preview/test.
        </p>
      )}

      {editing && (
        <ReportModal
          report={editing}
          setReport={setEditing}
          onCancel={() => setEditing(null)}
          onSave={commitEdit}
          clients={clients}
          clientGroups={clientGroups}
        />
      )}
    </div>
  );
}

function ReportModal({ report, setReport, onCancel, onSave, clients, clientGroups }) {
  const [newEmail, setNewEmail] = useState("");
  const set = (patch) => setReport(r => ({ ...r, ...patch }));

  const addEmail = () => {
    const e = newEmail.trim().toLowerCase();
    if (!e || !e.includes("@")) return toast.error("Enter a valid email");
    if ((report.recipients || []).includes(e)) return toast.error("Already on the list");
    set({ recipients: [...(report.recipients || []), e] });
    setNewEmail("");
  };

  const toggleDay = (d) => {
    const days = report.days || [];
    set({ days: days.includes(d) ? days.filter(x => x !== d) : [...days, d].sort((a, b) => a - b) });
  };

  const Switch = ({ on, onClick, label, hint }) => (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-[hsl(var(--border))]/40 last:border-0">
      <div>
        <p className="text-xs font-semibold">{label}</p>
        {hint && <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{hint}</p>}
      </div>
      <button onClick={onClick} className={`shrink-0 ${on ? "text-emerald-500" : "text-[hsl(var(--muted-foreground))]"}`}>
        {on ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
      </button>
    </div>
  );

  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-lg max-h-[86vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="w-4 h-4 text-sky-400" /> Client Report
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold block mb-1">Report name</label>
            <input
              value={report.label}
              onChange={e => set({ label: e.target.value })}
              placeholder="e.g. Baker Hughes — daily"
              className="w-full h-8 px-2.5 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="client-report-label-input"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold block mb-1.5">Covers</label>
            <div className="flex gap-1.5 mb-2">
              {["group", "client"].map(t => (
                <button
                  key={t}
                  onClick={() => set({ target_type: t, target: "" })}
                  className={`px-3 py-1 rounded-full text-[11px] font-bold border transition-colors ${
                    report.target_type === t
                      ? "bg-sky-500/15 text-sky-600 border-sky-500/30"
                      : "bg-[hsl(var(--muted))]/40 text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]"
                  }`}
                >
                  {t === "group" ? "Client group" : "Single client"}
                </button>
              ))}
            </div>
            <div className="max-h-40 overflow-y-auto rounded-md border border-[hsl(var(--border))] divide-y divide-[hsl(var(--border))]/50">
              {(report.target_type === "group" ? (clientGroups || []) : (clients || [])).map(o => {
                const val = report.target_type === "group" ? o.id : o.name;
                const active = report.target === val;
                return (
                  <button
                    key={o.id}
                    onClick={() => set({ target: val })}
                    className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-xs transition-colors ${
                      active ? "bg-sky-500/10 text-sky-600 font-semibold" : "hover:bg-[hsl(var(--muted))]/50"
                    }`}
                  >
                    <Mark svg={o.logo_svg} color={o.color} />
                    <span className="truncate">{o.name}</span>
                  </button>
                );
              })}
            </div>
            {report.target_type === "group" && (
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1.5">
                A group covers every client in it and its sub-groups — one report for Baker Hughes covers Wireline and FPS together.
              </p>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold block mb-1.5">Recipients</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(report.recipients || []).map(e => (
                <span key={e} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-[11px] bg-[hsl(var(--muted))] border border-[hsl(var(--border))]">
                  {e}
                  <button onClick={() => set({ recipients: report.recipients.filter(x => x !== e) })} className="hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {!(report.recipients || []).length && (
                <span className="text-[11px] text-amber-600">No recipients — this report can't send.</span>
              )}
            </div>
            <div className="flex gap-1.5">
              <input
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addEmail())}
                placeholder="name@company.com"
                className="flex-1 h-8 px-2.5 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <button onClick={addEmail} className="px-3 h-8 rounded-md text-xs font-semibold bg-[hsl(var(--muted))] border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/70">
                <Users className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div className="flex items-end gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold block mb-1">Send at (IST)</label>
              <input
                type="time"
                value={report.send_at}
                onChange={e => set({ send_at: e.target.value })}
                className="h-8 px-2.5 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="client-report-time-input"
              />
            </div>
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold block mb-1">Days</label>
              <div className="flex gap-1">
                {DAYS.map((d, i) => (
                  <button
                    key={d}
                    onClick={() => toggleDay(i)}
                    className={`w-8 h-8 rounded-md text-[10px] font-bold border transition-colors ${
                      (report.days || []).includes(i)
                        ? "bg-sky-500/15 text-sky-600 border-sky-500/30"
                        : "bg-[hsl(var(--muted))]/40 text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]"
                    }`}
                  >
                    {d[0]}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-[hsl(var(--border))] px-3">
            <Switch
              on={report.skip_if_quiet}
              onClick={() => set({ skip_if_quiet: !report.skip_if_quiet })}
              label="Skip quiet days"
              hint="Nothing moved and nothing overdue or blocked → don't send. Keeps the report worth opening."
            />
            <Switch
              on={report.show_value}
              onClick={() => set({ show_value: !report.show_value })}
              label="Show order value"
              hint="Line values, PO totals and the open-book value. Off strips all money from the email."
            />
            <Switch
              on={report.sections?.attention !== false}
              onClick={() => set({ sections: { ...report.sections, attention: report.sections?.attention === false } })}
              label="Needs attention section"
              hint="Blocked and overdue lines, including ones with no activity that day."
            />
            <Switch
              on={report.sections?.open_book !== false}
              onClick={() => set({ sections: { ...report.sections, open_book: report.sections?.open_book === false } })}
              label="Open book section"
              hint="Line count and value per stage."
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onCancel} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]">
              Cancel
            </button>
            <button
              onClick={onSave}
              className="px-4 py-1.5 rounded-lg text-xs font-bold text-white bg-orange-500 hover:bg-orange-600"
              data-testid="client-report-save-btn"
            >
              Done
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
